/**
 * Trennstelle between two zones (doc/Architektur/NETZVERBUND.md).
 * Mirrors backend Models/Zone/ZoneCoupling.cs (ZoneCouplingDto) — hand-maintained.
 * The coupling cable belongs to zone A and ends at a Kuppelpunkt sleeve; the switch/fuse sits
 * in a KVS or ONS of zone B. `isClosed` is the actual state (calculation input).
 */
export interface ZoneCoupling {
  id: string;
  zoneAId: string;
  zoneAName?: string | null;
  zoneBId: string;
  zoneBName?: string | null;
  cableId: string;
  cableName?: string | null;
  /** [lat, lng] pairs of the coupling cable (zone A element) */
  cableWaypoints: [number, number][];
  endpointElementId: string;
  switchElementId: string;
  switchElementType: 'kvs' | 'trafo';
  switchElementName?: string | null;
  switchBusbarId?: string | null;
  lat?: number | null;
  lng?: number | null;
  name: string;
  switchType: string;
  isClosed: boolean;
  normalClosed?: boolean | null;
  stateSource: string;
  stateChangedAt: string;
  stateChangedBy?: string | null;
  verbundId?: string | null;
}

export interface ZoneCouplingEvent {
  id: number;
  couplingId: string;
  changedAt: string;
  changedBy?: string | null;
  source: string;
  oldClosed?: boolean | null;
  newClosed: boolean;
  note?: string | null;
}

/** Calculation unit of a zone (GET /zones/{id}/couplings/verbund). */
export interface ZoneVerbund {
  id: string;
  zoneIds: string[];
  couplingIds?: string[];
  zones: { id: string; name: string }[];
}

/** Position of the Trennstelle marker: switch element, else the far end of the cable. */
export function couplingPosition(c: ZoneCoupling): [number, number] | null {
  if (c.lat != null && c.lng != null) return [c.lat, c.lng];
  const last = c.cableWaypoints[c.cableWaypoints.length - 1];
  return last ? [last[0], last[1]] : null;
}

/** The zone on the other side, seen from `zoneId`. */
export function otherZone(c: ZoneCoupling, zoneId: string): { id: string; name: string } {
  return c.zoneAId === zoneId
    ? { id: c.zoneBId, name: c.zoneBName ?? c.zoneBId }
    : { id: c.zoneAId, name: c.zoneAName ?? c.zoneAId };
}

/**
 * Load-flow figures of one Trennstelle (backend CouplingFlowDto, `couplingFlows` of
 * /powercalculation). The coupling cable belongs to zone A, so these values come from zone A's
 * result even while zone B is active. `powerKw` is signed A → B (positive = into zone B).
 */
export interface CouplingFlow {
  couplingId: string;
  cableId: string;
  zoneAId: string;
  zoneBId: string;
  isClosed: boolean;
  timestamp?: string | null;
  loadingPercent?: number | null;
  currentA?: number | null;
  currentL1A?: number | null;
  currentL2A?: number | null;
  currentL3A?: number | null;
  powerKw?: number | null;
  powerKvar?: number | null;
}

/** Active power leaving `zoneId` over the coupling in kW: positive = export to the neighbour, negative = import. */
export function flowOutOfZone(flow: CouplingFlow, zoneId: string): number | null {
  if (flow.powerKw == null) return null;
  return flow.zoneAId === zoneId ? flow.powerKw : -flow.powerKw;
}

/** Wording of the exchange seen from the active zone (`outKw` from {@link flowOutOfZone}). */
export function exchangeLabel(outKw: number | null, neighbourName: string): string {
  if (outKw == null) return 'Leistung unbekannt';
  if (Math.abs(outKw) < 0.05) return 'kein Austausch';
  return outKw > 0 ? `Abgabe an ${neighbourName}` : `Bezug aus ${neighbourName}`;
}
